import { bombSpec, columnDamagePerTick } from '../bombs';
import { livesCap } from '../constraints';
import {
  DT,
  HEAT_COOL_RATE,
  HEAT_MAX,
  HEAT_OVERHEAT,
  HEAT_PER_SHOT,
  HEAT_RECOVER,
  OVERDRIVE_FIRE_MUL,
  OVERHEAT_FIRE_MUL,
  OVERHEAT_JAM_SEC,
  PF_HH,
  PF_HW,
  ULTI_CHARGE_IFRAMES,
  ULTI_CHARGE_SEC,
  ULTI_SHOTS,
} from '../constants';
import { Ev } from '../events';
import {
  DRONE_BULLET_RADIUS_FRAC,
  DRONE_BULLET_SPEED_FRAC,
  DRONE_DMG_FRAC,
  DRONE_SLOTS,
  FEEL,
  HULL_GUNS,
} from '../FeelConstants';
import { approachFactor, clamp, cos, DEG, sin } from '../math';
import { Fam, PbMode } from '../pools/BulletPool';
import { penalizeDeath } from './progressionSystem';
import type { InputFrame, World } from '../World';

/** Vitesse de virage du tir primaire GUIDÉ (le Traqueur), en rad/s. Douce à
 *  dessein : la balle infléchit sa course vers l'ennemi sans le verrouiller —
 *  une saveur de visée, pas un gain de taux de touche qui ferait de l'arme un
 *  choix strictement supérieur, ce que l'équilibrage interdit. */
const PRIMARY_HOMING_RATE = 3.0;

/** Contacts que peut produire un tir PRIMAIRE perforant ou ricochet avant de
 *  mourir — une PETITE valeur, et c'est délibéré (voir le banc multi-cible). Trois
 *  traversées, dont la puissance décroît à chaque contact
 *  (`PRIMARY_PIERCE_FALLOFF`, dans collisionSystem) : une file de trois vaut
 *  1 + 0,6 + 0,36 = 1,96× le mono-cible, jamais 3×. */
const PRIMARY_PIERCE_HITS = 3;

/** Vie d'un tir PERFORANT primaire, en secondes. Volontairement TRÈS longue : la
 *  conduite `Pierce` est partagée avec la Scie, qui rentre au vaisseau une fois
 *  `life` écoulée — ce qu'un perforant ne doit JAMAIS faire. Il file tout droit et
 *  sort du cadre par le haut ; 30 s dépasse de très loin toute traversée d'écran,
 *  donc la branche de retour de `steerSecondaries` n'est jamais atteinte. */
const PRIMARY_PIERCE_LIFE = 30;

/** Vitesse de virage d'un tir RICOCHET primaire, en rad/s (≈ 1 600 °/s, comme
 *  l'Orage secondaire) : le trait se réoriente en quelques ticks vers l'ennemi
 *  suivant. Un virage lent dessinerait des courbes de missile au lieu d'une
 *  décharge qui SAUTE. */
const PRIMARY_ARC_TURN = 1600 * DEG;

/**
 * Équipe un projectile PRIMAIRE d'un type de balle non balistique, juste après sa
 * naissance — comme les secondaires à conduite. Chaque mode reçoit le bon triplet
 * (param, life, hits), et c'est ce qui sépare un sidegrade d'une arme cassée :
 *   · Homing (Traqueur)  — guidage doux, MEURT au premier contact (hits 1) : aucune
 *     traversée, donc aucun DPS multi-cible caché. Comportement d'origine inchangé.
 *   · Pierce (Perforant) — file tout droit et PERFORE une petite file. `param` 0
 *     (aucun guidage), `life` très longue pour qu'il ne rentre jamais au vaisseau.
 *   · Arc (Ricochet)     — SAUTE d'un ennemi au suivant à courte portée. `param`
 *     est la vitesse de virage vers la cible acquise.
 * Le budget de contacts (`PRIMARY_PIERCE_HITS`) et la décroissance de dégâts
 * (`PRIMARY_PIERCE_FALLOFF`) bornent ensemble le DPS multi-cible.
 */
function equipPrimaryBullet(pb: World['pb'], slot: number, mode: number): void {
  if (mode === PbMode.Pierce) {
    pb.behave(slot, mode, 0, PRIMARY_PIERCE_LIFE, PRIMARY_PIERCE_HITS);
  } else if (mode === PbMode.Arc) {
    pb.behave(slot, mode, PRIMARY_ARC_TURN, 0, PRIMARY_PIERCE_HITS);
  } else {
    pb.behave(slot, mode, PRIMARY_HOMING_RATE, 0, 1);
  }
}

/** Marges de déplacement. Le vaisseau ne peut pas sortir du terrain, et on lui
 *  interdit le tiers supérieur : un shmup où le joueur peut monter au contact
 *  des spawns supprime la lecture des vagues. */
const MARGIN_X = 20;
const MARGIN_BOTTOM = 30;
const MARGIN_TOP = 90;

/** Front montant. Une bombe se déclenche à l'APPUI, jamais tant que la touche
 *  est maintenue — sinon un joueur qui garde le doigt dessus vide son stock en
 *  trois ticks sans comprendre ce qui s'est passé. */
function bombPressed(p: { bombHeld: boolean }, input: InputFrame): boolean {
  return input.bomb && !p.bombHeld;
}

/**
 * Effet d'une bombe : efface les projectiles alentour et accorde une
 * invulnérabilité franche. La bombe est une SORTIE DE SECOURS — elle doit
 * toujours sauver, jamais « peut-être sauver », sinon le joueur ne s'en sert
 * plus et le système entier devient décoratif.
 *
 * ── CE QUI VARIE ENTRE LES TROIS BOMBES, ET CE QUI NE VARIE PAS ──
 *
 * ⚠️ L'EFFACEMENT ne varie pas. Il est le contrat, pas un paramètre : les trois
 * variantes purgent le même rayon de 900 u, qui couvre la quasi-totalité du
 * terrain. Faire varier ce rayon aurait donné une bombe qui sauve « parfois »,
 * et c'est exactement la promesse que le plan interdit de rompre — un joueur
 * qui doute de sa sortie de secours ne l'utilise plus, y compris quand elle
 * aurait marché.
 *
 * Varient : la durée d'invulnérabilité, et un effet de bord armé pour quelques
 * secondes (voir `src/sim/bombs.ts`).
 */
function useBomb(w: World): void {
  const p = w.player;
  p.bombs--;
  w.progress.bombsUsed++;

  const spec = bombSpec(p.bomb);
  /* ⚠️ On PROLONGE l'invulnérabilité, on ne l'écrase pas.
     L'écriture directe (`p.iframes = ...`) était sans conséquence tant que la
     bombe accordait 1,6 s, plus que tout le reste. Le BRISANT n'en donne que
     0,90 : bomber juste après une réapparition (1,4 s d'immunité) aurait alors
     RACCOURCI la protection d'une demi-seconde. Une bombe qui rend le joueur
     plus vulnérable qu'avant est la forme la plus retorse du « peut-être
     sauver ». */
  if (spec.iframes > p.iframes) p.iframes = spec.iframes;

  /* Effet de bord armé pour toutes les variantes ; ÉCLAT a une durée nulle,
     donc rien ne se déclenche et aucune branche n'est nécessaire ici. La
     colonne est figée à l'abscisse de l'appui — voir `PlayerState.bombFxX`. */
  p.bombFxT = spec.fx;
  p.bombFxX = p.x;

  const eb = w.eb;
  const r2 = FEEL.bombClearRadius * FEEL.bombClearRadius;
  for (let i = eb.count - 1; i >= 0; i--) {
    const s = eb.at(i);
    const dx = eb.x[s]! - p.x;
    const dy = eb.y[s]! - p.y;
    if (dx * dx + dy * dy < r2) {
      w.events.push(Ev.BulletBlocked, s, eb.x[s]!, eb.y[s]!, 0);
      eb.free(s);
    }
  }
}

/**
 * Décompte de l'effet de bord de la bombe, et dégâts de la colonne du BRISANT.
 *
 * Appelé AVANT le retour anticipé de la mort du joueur : une colonne allumée
 * continue de brûler pendant la réapparition. L'inverse punirait deux fois — on
 * perd une vie ET on perd les dégâts déjà payés d'une bombe.
 *
 * ⚠️ Aucun événement par tick. La tentation était de pousser un `EnemyDamaged`
 * à chaque tick et par ennemi touché : sur 264 ticks et une dizaine d'ennemis,
 * l'anneau de 512 événements déborderait et la présentation perdrait des morts
 * réelles. La vue reçoit la colonne UNE fois, à l'appui, par `Ev.BombUsed` dont
 * le champ `a` porte désormais la variante.
 */
function bombFx(w: World): void {
  const p = w.player;
  const t = p.bombFxT;
  if (t <= 0) return;

  const dmg = columnDamagePerTick(p.bomb, DT);
  if (dmg > 0) {
    const half = bombSpec(p.bomb).columnHalf;
    const en = w.enemies;
    const x0 = p.bombFxX;
    for (let i = 0; i < en.count; i++) {
      const s = en.at(i);
      // Un ennemi encore en télégraphe est intouchable : la bombe ne fait pas
      // exception, sinon elle détruirait des vagues avant qu'elles n'entrent en
      // scène et le télégraphe cesserait de télégraphier quoi que ce soit.
      if (en.telegraph[s]! > 0) continue;
      // Déjà mort ce tick : `damageSystem` ne l'a pas encore libéré. Le surplus
      // serait perdu et l'événement compté deux fois.
      const hp = en.hp[s]!;
      if (hp <= 0) continue;
      /* Le blindage par les parties tient AUSSI contre la bombe. C'est une
         règle de structure de combat — « casser les armes avant le porteur » —
         et une bombe qui la contournerait offrirait un raccourci de boss à
         500 crédits. Le bouclier DIRECTIONNEL, lui, ne s'applique pas : il
         décrit un angle d'attaque, et une colonne n'attaque pas depuis un
         angle. */
      if (en.shielded[s] === 1 && en.livingParts(s) > 0) continue;

      const dx = en.x[s]! - x0;
      if (dx < -half || dx > half) continue;
      en.hp[s] = hp - dmg;
    }
  }

  const left = t - DT;
  p.bombFxT = left > 0 ? left : 0;
}

/**
 * Régénération du bouclier joueur, et synchronisation de sa capacité.
 *
 * ⚠️ `shieldMax` est un MIROIR de la stat `maxShield`, recopié ICI à chaque tick
 * plutôt que posé une fois : une carte de capacité ramassée en cours de partie
 * relève le plafond aussitôt, sans qu'aucun hook d'application n'ait à recopier
 * la valeur. Zéro pour un joueur sans amélioration → le bloc entier est inerte,
 * et la difficulté de série reste exactement ce qu'elle était.
 *
 * La jauge ne remonte qu'après `shieldRegenCd` secondes SANS coup encaissé
 * (`collisionSystem` remet ce délai à plein à chaque absorption) : un joueur sous
 * le feu ne recharge pas, sinon le bouclier annulerait des rideaux entiers au
 * lieu de sauver un instant précis.
 */
function regenShield(w: World): void {
  const p = w.player;
  const max = w.progress.stats.maxShield;
  p.shieldMax = max;

  if (max <= 0) {
    // Aucune capacité : rien à régénérer. On ne laisse pas non plus traîner un
    // reliquat si la capacité vient d'être PERDUE (une mort ampute le build, donc
    // les cartes de bouclier, donc `maxShield`).
    if (p.shield !== 0) p.shield = 0;
    p.shieldRegenCd = 0;
    return;
  }

  // Capacité réduite en cours de route : on retombe au plafond, jamais au-dessus.
  if (p.shield > max) p.shield = max;

  if (p.shieldRegenCd > 0) {
    let cd = p.shieldRegenCd - DT;
    if (cd < 0) cd = 0;
    p.shieldRegenCd = cd;
    return;
  }

  if (p.shield < max) {
    const s = p.shield + w.progress.stats.shieldRegen * DT;
    p.shield = s < max ? s : max;
  }
}

export function playerSystem(w: World, input: InputFrame): void {
  const p = w.player;

  if (p.iframes > 0) {
    p.iframes -= DT;
    if (p.iframes < 0) p.iframes = 0;
  }

  /* ── Coque de verre : plafond de vies RÉAPPLIQUÉ à chaque tick ──────────
     La contrainte promet deux choses, et la seconde est celle qu'on oublie :
     « une seule vie, ET aucun gain de vie de la run ». `armPlayer` ne peut
     tenir que la première — elle écrit une fois, au démarrage.

     Le plafond vit donc ici, dans la boucle. Aujourd'hui rien n'accorde de vie
     en cours de partie ; le jour où un ramassable, une carte ou la boutique de
     bord le fera, la contrainte tiendra sans que personne n'ait à y penser. Un
     joueur qui a payé 850 crédits pour jouer sans filet ne doit pas découvrir
     que le filet est revenu par une porte de service.

     Le plafond ne remonte jamais rien : `lives` à 0 (mort actée) reste à 0. */
  const cap = livesCap(w.run.constraints);
  if (cap > 0 && p.lives > cap) p.lives = cap;

  // Avant le retour anticipé de la mort : une colonne allumée continue de
  // brûler pendant la réapparition.
  bombFx(w);

  /* ⚠️ AUCUNE réparation automatique de l'escorte. Retour de jeu : « si un drone
     se fait toucher, il finit par réapparaître, il ne faut pas ». Un drone
     touché reste HORS SERVICE pour toute la vie en cours — c'est un débit perdu
     qu'on ne récupère qu'en réapparaissant. Protéger ses drones devient un vrai
     enjeu, et non un bonus qui se régénère tout seul dès qu'on les néglige. */

  if (p.dead) {
    p.deathT += DT;
    if (p.deathT >= FEEL.respawnDelay) {
      p.dead = false;
      p.deathT = 0;
      // Réapparition à l'abscisse de la mort, en bas de terrain. Le CONTRÔLE
      // EST RENDU IMMÉDIATEMENT (PLAN.md §6.2) : aucune animation d'entrée ne
      // prive le joueur de ses commandes, même une demi-seconde.
      p.y = -PF_HH + 150;
      p.px = p.x;
      p.py = p.y;
      p.vx = 0;
      p.vy = 0;
      p.iframes = FEEL.iframes;
      // La nouvelle vie rend l'escorte intacte : la sanction du drone touché
      // vaut pour UNE vie, elle ne se cumule pas d'une mort à l'autre. La mort
      // a déjà son propre coût, bien plus lourd (voir `penalizeDeath`).
      p.droneDown.fill(0);
      // Bouclier rendu à PLEIN pour la nouvelle vie — à hauteur de la capacité
      // qui SUBSISTE après l'amputation du build par `penalizeDeath` (une mort a
      // pu emporter des cartes de bouclier). Réapparaître avec une barrière vide
      // enverrait le joueur droit dans le rideau qui vient de le tuer.
      p.shield = w.progress.stats.maxShield;
      p.shieldRegenCd = 0;
      w.events.push(Ev.PlayerRespawned, 0, p.x, p.y, 0);
    }
    return;
  }

  // Régénération du bouclier : tick de joueur VIVANT uniquement (le retour
  // ci-dessus a filtré la mort). Elle tourne même pendant la fenêtre de
  // death-bomb — le joueur est encore aux commandes, donc encore protégeable.
  regenShield(w);

  /* ── fenêtre de death-bomb (PLAN.md §6.3) ──────────────────────────────
     Le joueur vient d'être touché, mais la mort n'est pas encore actée. Pendant
     ces 150 ms il continue de jouer normalement — et s'il bombe, il s'en sort.

     C'est le mécanisme le plus rentable du genre et le plus systématiquement
     oublié : il ne change rien pour qui ne le connaît pas, et il transforme
     des dizaines de morts subies en morts rattrapées de justesse pour qui le
     découvre sans qu'on le lui ait jamais dit. */
  if (p.deathBombT > 0) {
    p.deathBombT -= DT;

    if (bombPressed(p, input) && p.bombs > 0) {
      p.deathBombT = 0;
      useBomb(w);
      // `a` porte la VARIANTE de bombe : c'est la seule information dont la
      // présentation a besoin pour dessiner la bonne détonation, et elle tient
      // dans un champ déjà présent. Un type d'événement de plus aurait obligé
      // l'audio et le rendu à traiter deux cas là où ils en traitent un.
      w.events.push(Ev.DeathBombed, p.bomb, p.x, p.y, p.bombs);
    } else if (p.deathBombT <= 0) {
      // La fenêtre s'est refermée : la mort est actée maintenant.
      p.deathBombT = 0;
      p.dead = true;
      p.deathT = 0;
      p.vx = 0;
      p.vy = 0;
      p.lives--;
      w.progress.deaths++;
      // ⚠️ La mort entame le BUILD, pas seulement le compteur de vies. Sans ça,
      // un joueur maxé ne risquait plus rien et « attendait » (retour de jeu).
      penalizeDeath(w);
      w.events.push(Ev.PlayerDied, 0, p.x, p.y, p.lives);
      return;
    }
  } else if (bombPressed(p, input) && p.bombs > 0) {
    useBomb(w);
    w.events.push(Ev.BombUsed, p.bomb, p.x, p.y, p.bombs);
  }
  p.bombHeld = input.bomb;

  /* ── ULTI (super-attaque chargée) ────────────────────────────────────────
     Déclenchement au FRONT, jauge pleine, et pas déjà en charge ni en décharge.
     La CHARGE est un télégraphe (invulnérable) ; à son terme, on ARME la rafale
     de décharge, que `secondarySystem` tire ensuite (il conduit et émet les
     projectiles). La secondaire choisie au moment de la décharge est celle qui
     part — changer d'arme pendant la charge change donc l'ulti. */
  if (input.ulti && !p.ultiHeld && p.ulti >= 1 && p.ultiChargeT <= 0 && p.ultiShots <= 0) {
    p.ulti = 0;
    p.ultiChargeT = ULTI_CHARGE_SEC;
    if (p.iframes < ULTI_CHARGE_IFRAMES) p.iframes = ULTI_CHARGE_IFRAMES;
    w.events.push(Ev.UltiCharge, 0, p.x, p.y, 0);
  }
  p.ultiHeld = input.ulti ?? false;

  if (p.ultiChargeT > 0) {
    p.ultiChargeT -= DT;
    if (p.ultiChargeT <= 0) {
      p.ultiChargeT = 0;
      p.ultiShots = ULTI_SHOTS; // la rafale démarre ; secondarySystem la tire
      p.ultiShotT = 0; // première volée sur-le-champ
      w.events.push(Ev.UltiDischarge, p.secondary, p.x, p.y, 0);
    }
  }

  /* ── déplacement ─────────────────────────────────────────────────────── */
  let ix = input.moveX;
  let iy = input.moveY;

  /* Pilotage au POINTEUR. Le vaisseau se dirige vers la cible plutôt que d'y
     être téléporté : il conserve ainsi l'inertie, le dérapage et le banking
     calibrés au J0. Une souris qui téléporterait rendrait toutes ces valeurs
     caduques et la sensation serait entièrement différente selon le
     périphérique — ce qui est le pire des deux mondes.

     Le clavier reste prioritaire dès qu'une touche est enfoncée : un joueur qui
     passe de l'un à l'autre ne doit jamais lutter contre un curseur oublié. */
  if (input.aimX !== undefined && input.aimY !== undefined && ix === 0 && iy === 0) {
    const dx = input.aimX - p.x;
    const dy = input.aimY - p.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    // Zone morte : sans elle, le vaisseau vibre autour du curseur au repos.
    if (d > 4) {
      const inv = 1 / d;
      // Approche proportionnelle bornée : loin du curseur on va à pleine
      // vitesse, tout près on ralentit — ce qui donne un arrêt net sans
      // dépassement.
      const k = d < 90 ? d / 90 : 1;
      ix = dx * inv * k;
      iy = dy * inv * k;
    }
  }

  const l2 = ix * ix + iy * iy;
  // Normalisation seulement au-delà du cercle unité : une manette qui rend
  // 0,5 doit donner la moitié de la vitesse, alors qu'une diagonale au clavier
  // (1, 1) ne doit pas donner 1,41× la vitesse.
  if (l2 > 1) {
    const l = Math.sqrt(l2);
    ix /= l;
    iy /= l;
  }

  /* ⚠️ On lit les statistiques EFFECTIVES, jamais les constantes `FEEL`.
     Celles-ci ne sont plus qu'un point de départ : `progress.stats` y ajoute
     les cartes ramassées et les bonus de méta-progression.
     Lire `FEEL` ici — ce que faisait la version précédente — rendait TOUT le
     système d'améliorations décoratif : les cartes s'accumulaient sans le
     moindre effet sur le jeu. */
  const st = w.progress.stats;
  const speed = st.moveSpeed * (input.focus ? FEEL.focusMul : 1);
  const moving = ix !== 0 || iy !== 0;
  const k = approachFactor(DT, moving ? FEEL.tauAccel : FEEL.tauDecel);

  p.vx += (ix * speed - p.vx) * k;
  p.vy += (iy * speed - p.vy) * k;
  p.x += p.vx * DT;
  p.y += p.vy * DT;

  p.x = clamp(p.x, -PF_HW + MARGIN_X, PF_HW - MARGIN_X);
  p.y = clamp(p.y, -PF_HH + MARGIN_BOTTOM, PF_HH - MARGIN_TOP);

  p.fireCd -= DT;
  if (p.fireCd < 0) p.fireCd = 0;

  // SURALIMENTATION (power-up Overdrive) : compte à rebours. Tant qu'elle dure, le
  // canon est GLACÉ et la cadence doublée (voir plus bas).
  p.overdriveT -= DT;
  if (p.overdriveT < 0) p.overdriveT = 0;
  const overdrive = p.overdriveT > 0;

  /* ── CHAUFFE du canon ─────────────────────────────────────────────────────
     Refroidissement PERMANENT (le tir le recompense) puis bascule de surchauffe
     à HYSTÉRÉSIS : on n'y entre qu'à saturation, on n'en sort qu'après avoir
     vraiment refroidi — sinon on clignoterait à la frontière. La suralimentation
     court-circuite tout : canon froid, jamais en surchauffe, jamais enrayé. */
  if (overdrive) {
    p.heat = 0;
    p.overheated = false;
    p.jammed = false;
    p.overheatT = 0;
  } else {
    p.heat -= HEAT_COOL_RATE * DT;
    if (p.heat < 0) p.heat = 0;
    if (p.heat >= HEAT_OVERHEAT) {
      if (!p.overheated) w.events.push(Ev.Overheat, 0, p.x, p.y, 0);
      p.overheated = true;
    } else if (p.overheated && p.heat <= HEAT_RECOVER) {
      // Refroidi : on sort de surchauffe ET on libère l'enrayage d'un coup.
      p.overheated = false;
      p.jammed = false;
      p.overheatT = 0;
    }
    /* ENRAYAGE : rester en rouge vif EN TIRANT accumule un dwell ; passé le seuil,
       le canon se BLOQUE net (fumée) jusqu'au refroidissement complet ci-dessus.
       Le compteur n'avance qu'en TIRANT — relâcher en surchauffe l'arrête, donc la
       discipline évite l'enrayage. Une fois enrayé, on ne tire plus, donc le
       compteur cesse de monter : le seul chemin de sortie est le refroidissement. */
    if (p.overheated && !p.jammed && input.fire) {
      p.overheatT += DT;
      if (p.overheatT >= OVERHEAT_JAM_SEC) {
        p.jammed = true;
        w.events.push(Ev.Jam, 0, p.x, p.y, 0);
      }
    }
  }

  /* ── tir MANUEL ───────────────────────────────────────────────────────────
     Le tir primaire n'est plus automatique (revirement §6.3 assumé par le
     joueur) : on APPUIE (clic gauche) pour tirer, et chaque coup CHAUFFE le
     canon. En surchauffe la cadence s'effondre ; ENRAYÉ, plus rien ne part ; en
     suralimentation la cadence double. */
  if (input.fire && !p.jammed && p.fireCd <= 0) {
    const cadenceMul = overdrive ? OVERDRIVE_FIRE_MUL : p.overheated ? OVERHEAT_FIRE_MUL : 1;
    p.fireCd += (1 / st.fireRate) * cadenceMul;
    // Garde-fou : si la cadence devenait absurde, on ne veut pas d'une boucle
    // qui rattrape indéfiniment un retard.
    if (p.fireCd < 0) p.fireCd = 0;

    /* Les deux premiers projectiles sortent des canons de FUSELAGE, serrés
       autour de l'axe. Tous les suivants sortent d'un DRONE.

       C'est ce qui manquait : la gerbe s'élargissait de quinze unités par
       amélioration, si bien qu'un joueur qui prenait « Drone d'escorte » voyait
       exactement la même chose qu'avant, en un peu plus épais. Désormais un
       projectile de plus, c'est un appareil de plus dans la formation — la
       montée en puissance se VOIT avant de se mesurer. */
    const n = st.bulletCount;
    const hull = n < HULL_GUNS ? n : HULL_GUNS;
    const drones = n - hull < DRONE_SLOTS.length ? n - hull : DRONE_SLOTS.length;

    /* Répartition des dégâts. Chaque drone touche `DRONE_DMG_FRAC` de la part
       moyenne ; le fuselage absorbe TOUT le reste, y compris la part d'un
       éventuel projectile acheté au-delà du dernier emplacement de drone. La
       somme d'une volée à pleine escorte vaut donc exactement `n × damage`, et
       le DPS que mesure le banc d'équilibrage — `cadence × nombre × dégâts` —
       ne bouge pas d'un iota.

       ⚠️ La SEULE chose qui fait descendre le débit réel sous ce nominal est un
       drone hors service : il ne tire pas, mais le fuselage ne compense pas sa
       perte. C'est voulu — c'est exactement le coût de l'escorte vulnérable, et
       le fuselage qui rattraperait le trou annulerait la contrepartie d'un
       drone touché. */
    const droneDmg = st.damage * DRONE_DMG_FRAC;
    const hullDmg = hull > 0 ? (n * st.damage - drones * droneDmg) / hull : 0;
    const droneR = st.bulletRadius * DRONE_BULLET_RADIUS_FRAC;
    const droneSpeed = st.bulletSpeed * DRONE_BULLET_SPEED_FRAC;

    /* TYPE de balle du tir primaire (`stats.bulletMode`, posé par l'arme). Borné
       au domaine de PbMode par prudence : une donnée corrompue ne doit pas
       désigner une conduite inexistante. 0 = balistique, aucun marquage — le tir
       de série reste exactement ce qu'il était (donc golden intact). */
    const bulletMode = st.bulletMode > 0 ? (st.bulletMode < 4 ? Math.floor(st.bulletMode) : 4) : 0;

    /* ÉVENTAIL : ouverture angulaire posée par l'arme (`bulletSpread`, en degrés).
       Elle ne concerne QUE le fuselage — les drones tirent depuis des emplacements
       fixes en V et gardent leur axe. */
    const spreadRad = st.bulletSpread * DEG;

    for (let i = 0; i < hull; i++) {
      const offset = (i - (hull - 1) / 2) * 15;
      /* ⚠️ GARDE golden : à spread nul (toutes les armes sauf l'Éventail), on
         conserve `vx = 0` et `vy = bulletSpeed` LITTÉRAUX plutôt que
         `sin(0) × v`/`cos(0) × v`. La trigonométrie tabulée ne rend pas
         exactement 0 et 1, et la moindre différence sur la vitesse d'un tir
         primaire décalerait l'empreinte de replay — donc re-bénédiction du
         golden pour rien. */
      let vx = 0;
      let vy = st.bulletSpeed;
      if (spreadRad !== 0 && hull > 1) {
        // Répartition symétrique de l'éventail : t ∈ [−0,5, +0,5] mappé sur ± la
        // demi-ouverture. Deux canons → un tir à −spread/2 et un à +spread/2.
        const a = (i / (hull - 1) - 0.5) * spreadRad;
        vx = sin(a) * st.bulletSpeed;
        vy = cos(a) * st.bulletSpeed;
      }
      const slot = w.pb.spawn(p.x + offset, p.y + 30, vx, vy, st.bulletRadius, hullDmg, Fam.Player);
      if (slot < 0) w.overflow.playerBullets++;
      // Balle d'un type particulier : on la marque APRÈS la naissance, comme les
      // secondaires à conduite. Perforant/ricochet survivent au contact mais sont
      // bornés (budget de contacts + décroissance) ; le guidage meurt au premier.
      else if (bulletMode !== PbMode.Ballistic) equipPrimaryBullet(w.pb, slot, bulletMode);
    }
    for (let i = 0; i < drones; i++) {
      // Un drone hors service ne tire pas. C'est TOUT le coût d'un drone
      // touché — pas de vie perdue, pas de perte définitive : du débit en
      // moins, le temps de le réparer.
      if (p.droneDown[i]! > 0) continue;
      const d = DRONE_SLOTS[i]!;
      const slot = w.pb.spawn(
        p.x + d.x,
        p.y + d.y + 22,
        0,
        droneSpeed,
        droneR,
        droneDmg,
        Fam.PlayerDrone,
      );
      if (slot < 0) w.overflow.playerBullets++;
      else if (bulletMode !== PbMode.Ballistic) equipPrimaryBullet(w.pb, slot, bulletMode);
    }

    // Chaque volée CHAUFFE le canon, plafonné à HEAT_MAX (au-DESSUS du seuil de
    // surchauffe : c'est ce qui permet à un tir soutenu de franchir la ligne
    // malgré le refroidissement de tête de tick) — SAUF en suralimentation, où le
    // canon reste glacé. La bascule en surchauffe est décidée en tête de fonction,
    // au tick SUIVANT (latence imperceptible qui garde la logique en un lieu).
    if (!overdrive) {
      p.heat += HEAT_PER_SHOT;
      if (p.heat > HEAT_MAX) p.heat = HEAT_MAX;
    }
  }
}
